import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Orbit, RotateCcw, Maximize2 } from "lucide-react";
import { ViewerHost } from "./ViewerHost";
import { Loader } from "@/shared/ui";
import type { ViewerClient } from "../client/ViewerClient";
import type { MaterialKind } from "../shared/api-contract";
import { getModelGlbUrl, type EmbedOptions } from "../features/vault/vaultApi";
import {
  correctConfiguratorKinds,
  defaultIndexOf,
  effectiveConfigurator,
  matchGroupTargets,
  slotKindOf,
  type ConfiguratorDef,
  type ConfiguratorOptionGroup,
  type ConfiguratorVariant,
  type OptionKind,
} from "../features/vault/configurator";
import { applyOption } from "../features/vault/applyOption";
import { capsFor } from "../ui/editor/caps";
import { withEmbeddedScene } from "../util/embeddedScene";
import { CustomerBar } from "../ui/configurator/CustomerBar";

/** Resolved data needed to render an interactive configurator (embed/showcase). */
export interface ResolvedConfig {
  glbUrl: string;
  format: string;
  posterUrl: string | null;
  config: { vjson?: string; configurator?: ConfiguratorDef } | null;
  name: string;
  /** White-label / viewer settings from the embed (absent on showcase). */
  options?: EmbedOptions;
}

/**
 * Self-contained interactive configurator: mounts the viewer, loads the model +
 * saved config, renders the swatch/price panel, applies selections live, and
 * speaks the host postMessage protocol (ready/configChange + inbound select).
 * Used by the public embed AND the showcase detail view.
 */
export default function ConfiguratorView({ data }: { data: ResolvedConfig }) {
  const bg = data.options?.background;
  const colored = bg?.type === "color";
  return (
    <ViewerHost mode="ring">
      {({ ready, client, iframe }) => (
        <div
          className={`relative h-full w-full overflow-hidden ${colored ? "" : "bg-ink-950"}`}
          style={colored ? { background: bg?.color } : undefined}
        >
          {iframe}
          <Configurator ready={ready} client={client} data={data} />
        </div>
      )}
    </ViewerHost>
  );
}

/** A group resolved against the live viewer (label → real group id). */
interface LiveGroup extends ConfiguratorOptionGroup {
  groupId: string;
  activeIndex: number;
  /**
   * What the part IS (`slotKindOf`: its name, else the def's kind, else what
   * the viewer reports) — as opposed to `kinds`, what it is WEARING. A colour
   * choice is only ever put on a metal slot.
   */
  slotKind: OptionKind;
}

/** Each matched group, live: its viewer id, its opening choice and its slot kind. */
function liveGroupsOf(matched: { group: ConfiguratorOptionGroup; target: { id: string; label: string; kind: string } }[]): LiveGroup[] {
  return matched.map(({ group, target }) => ({
    ...group,
    groupId: target.id,
    activeIndex: defaultIndexOf(group),
    slotKind: slotKindOf({ label: target.label, kind: target.kind }, group.kind),
  }));
}

function Configurator({
  ready,
  client,
  data,
}: {
  ready: boolean;
  client: ViewerClient | null;
  data: ResolvedConfig;
}) {
  const [groups, setGroups] = useState<LiveGroup[]>([]);
  const [variants, setVariants] = useState<ConfiguratorVariant[]>([]);
  const [activeVariant, setActiveVariant] = useState(0);
  const [swapping, setSwapping] = useState(false);
  const [meta, setMeta] = useState<{ basePrice?: number; currency: string }>({ currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [autoRotate, setAutoRotate] = useState(!!data.options?.autoRotate);
  const reduce = useReducedMotion();
  /**
   * What each viewer group IS right now (metal or stone), by group id. A
   * library choice can change it — a .dmat makes a stone of a metal part — and
   * the next choice routes on it. Read and written only inside `queue`, so it
   * always describes the model after every click before it.
   */
  const kinds = useRef(new Map<string, MaterialKind>());
  /** Material changes run one at a time, in click order. */
  const queue = useRef<Promise<void>>(Promise.resolve());
  const loaded = useRef(false);
  const groupsRef = useRef<LiveGroup[]>([]);
  const metaRef = useRef(meta);
  const variantsRef = useRef<ConfiguratorVariant[]>([]);
  const activeVariantRef = useRef(0);
  groupsRef.current = groups;
  metaRef.current = meta;
  variantsRef.current = variants;
  activeVariantRef.current = activeVariant;

  /** Post the current configuration to the host page (SDK integration hook). */
  const emitChange = (gs: LiveGroup[], currency: string, basePrice?: number) => {
    const price = (basePrice ?? 0) + gs.reduce((s, g) => s + (g.options[g.activeIndex]?.priceDelta ?? 0), 0);
    const selections = gs.map((g) => ({
      group: g.label,
      optionId: g.options[g.activeIndex]?.id,
      option: g.options[g.activeIndex]?.name,
    }));
    const variant = variantsRef.current[activeVariantRef.current];
    window.parent?.postMessage(
      { kind: "jewelitt:configChange", name: data.name, price, currency, selections, variant: variant?.label, modelId: variant?.modelId },
      "*",
    );
  };

  /** Put each listed group's active option on the model, in order, after anything already queued. */
  const enqueueApply = (c: ViewerClient, gs: LiveGroup[]): Promise<void> => {
    const job = queue.current.then(async () => {
      for (const g of gs) {
        const opt = g.options[g.activeIndex];
        if (!opt) continue;
        const was = kinds.current.get(g.groupId) ?? (g.kind === "gem" ? "gem" : "metal");
        try {
          kinds.current.set(g.groupId, await applyOption(c, g.groupId, opt, was, g.slotKind));
        } catch {
          /* one material that will not load must not strand the rest */
        }
      }
    });
    queue.current = job.catch(() => undefined);
    return job;
  };

  useEffect(() => {
    if (!ready || !client || loaded.current) return;
    loaded.current = true;
    (async () => {
      try {
        const native = ["3dm", "obj", "fbx", "stl"].includes(data.format);
        // v65: the link's saved scene wins over one inside the file; without one, the file's own look shows.
        await client.ring.addModelUrl(
          data.glbUrl,
          withEmbeddedScene(native ? { autoCenter: true, autoScale: true } : undefined, capsFor(client.contractVersion).sceneFileV65, !!data.config?.vjson),
        );

        // Host-side embed options (no viewer contract change).
        if (data.options?.background?.type === "color") {
          try {
            await client.ring.setTransparentBackground();
          } catch {
            /* optional */
          }
        }
        if (data.options?.autoRotate) {
          try {
            await client.ring.setAutoRotate(true, data.options.autoRotateSpeed ?? 1);
          } catch {
            /* optional */
          }
        }

        if (data.config?.vjson) {
          try {
            await client.ring.loadVjsonFile(
              new File([data.config.vjson], "config.vjson", { type: "application/json" }),
            );
          } catch {
            /* scene/camera config best-effort */
          }
        }

        const viewerGroups = await client.ring.listGroups();
        const authored = !!data.config?.configurator?.groups?.length;
        // Read with each part's kind as its NAME says: a def saved while
        // "Gem 01" was showing as metal still configures a stone.
        const def: ConfiguratorDef = effectiveConfigurator(correctConfiguratorKinds(data.config?.configurator), viewerGroups);

        // A group the designer emptied is persisted but has nothing to show.
        const matched = matchGroupTargets(def.groups, viewerGroups).filter(({ group }) => group.options.length > 0);
        kinds.current = new Map(matched.map(({ target }) => [target.id, target.kind]));
        const live: LiveGroup[] = liveGroupsOf(matched);
        // The saved "Opens on" option is what the swatches highlight and what the
        // price is summed from — so the model has to actually wear it. Without
        // this the embed opened showing Rose Gold selected on a ring still
        // rendering its GLB material. Only for an AUTHORED def: the auto-built
        // fallback's index-0 default is a guess, and forcing it would restyle
        // every model nobody has configured yet.
        if (authored) await enqueueApply(client, live);
        const currency = def.currency ?? "USD";
        setMeta({ basePrice: def.basePrice, currency });
        setGroups(live);
        setVariants(def.variants ?? []);
        // emitChange reads these refs; assign now (state setters haven't re-rendered yet)
        // so the initial ready/configChange reports the base variant, not undefined.
        variantsRef.current = def.variants ?? [];
        activeVariantRef.current = 0;
        window.parent?.postMessage({ kind: "jewelitt:ready", name: data.name }, "*");
        emitChange(live, currency, def.basePrice);
      } catch {
        /* leave panel empty on failure */
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, client, data]);

  const applyIndex = async (groupId: string, idx: number) => {
    if (!client) return;
    const gs = groupsRef.current;
    const g = gs.find((x) => x.id === groupId);
    const opt = g?.options[idx];
    if (!g || !opt) return;
    const next = gs.map((x) => (x.id === groupId ? { ...x, activeIndex: idx } : x));
    setGroups(next);
    emitChange(next, metaRef.current.currency, metaRef.current.basePrice);
    await enqueueApply(client, [{ ...g, activeIndex: idx }]);
  };

  // Switch the whole model. Groups are re-resolved by label against the new
  // model, and the customer's current material selections are re-applied.
  const selectVariant = async (vi: number) => {
    if (!client) return;
    const v = variantsRef.current[vi];
    const prev = activeVariantRef.current;
    if (!v || vi === prev || swapping) return;
    setSwapping(true);
    setActiveVariant(vi);
    try {
      const { url } = await getModelGlbUrl(v.modelId);
      await client.ring.swapModel(url);
      const viewerGroups = await client.ring.listGroups();
      const rematched = matchGroupTargets(groupsRef.current, viewerGroups);
      // A new model: its groups are what IT says they are.
      kinds.current = new Map(rematched.map(({ target }) => [target.id, target.kind]));
      // The shopper's current choice per part rides over to the new model.
      const relive: LiveGroup[] = liveGroupsOf(rematched).map((g, i) => ({ ...g, activeIndex: rematched[i].group.activeIndex }));
      setGroups(relive);
      await enqueueApply(client, relive);
      emitChange(relive, metaRef.current.currency, metaRef.current.basePrice);
    } catch {
      // Swap failed (e.g. the variant model was deleted) — keep the current model
      // on screen and revert the highlight so the user isn't stuck on a dead choice.
      setActiveVariant(prev);
    } finally {
      setSwapping(false);
    }
  };

  // Inbound control: host page → embed (the SDK's .select()/.getState()).
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { kind?: string; group?: string; option?: string };
      if (!d || typeof d !== "object") return;
      if (d.kind === "jewelitt:getState") {
        emitChange(groupsRef.current, metaRef.current.currency, metaRef.current.basePrice);
        return;
      }
      if (d.kind === "jewelitt:select") {
        const gs = groupsRef.current;
        const gk = String(d.group ?? "").toLowerCase();
        const g = gs.find((x) => x.id === d.group || x.targetGroupLabel === d.group || x.label.toLowerCase() === gk);
        if (!g) return;
        const ok = String(d.option ?? "").toLowerCase();
        const idx = g.options.findIndex((o) => o.id === d.option || o.name.toLowerCase() === ok);
        if (idx >= 0) void applyIndex(g.id, idx);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const opts = data.options;

  // ── Floating viewport toolbar (iJewel-style) — all via existing RPCs ──
  const toggleTurntable = async () => {
    if (!client) return;
    const next = !autoRotate;
    setAutoRotate(next);
    try { await client.ring.setAutoRotate(next, data.options?.autoRotateSpeed ?? 1); } catch { /* optional */ }
  };
  const resetView = async () => {
    if (!client) return;
    try { await client.ring.setCameraPreset("threeQuarter"); } catch { /* optional */ }
  };
  const goFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.();
  };

  return (
    <>
      <AnimatePresence>
        {(!ready || loading) && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-20 flex items-center justify-center bg-ink-950"
          >
            {data.posterUrl ? (
              <img src={data.posterUrl} alt="" className="max-h-[70%] max-w-[70%] object-contain opacity-60" />
            ) : (
              <Loader variant={opts?.loaderStyle ?? "spinner"} logo={opts?.logo?.dataUrl} tone="gold" size={30} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Title + white-label logo overlays (embed options) */}
      {ready && !loading && opts?.showTitle && (
        <span className="pointer-events-none absolute left-3 top-3 z-30 rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-semibold text-ink-800 shadow-sm backdrop-blur">
          {data.name}
        </span>
      )}
      {ready && !loading && opts?.logo?.show && opts.logo.dataUrl && (
        <img
          src={opts.logo.dataUrl}
          alt=""
          className={`pointer-events-none absolute top-3 z-30 h-8 ${
            opts.logo.position === "right" ? "right-3" : opts.logo.position === "center" ? "left-1/2 -translate-x-1/2" : "left-3"
          } ${opts.logo.fit === "cover" ? "object-cover" : "object-contain"}`}
        />
      )}

      {/* Floating viewport toolbar — right-center, iJewel-style glass rail */}
      {ready && !loading && (
        <motion.div
          initial={reduce ? false : { opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="absolute right-3 top-1/2 z-30 flex -translate-y-1/2 flex-col gap-1 rounded-full border border-white/60 bg-white/70 p-1 shadow-pop backdrop-blur-xl"
        >
          {[
            { key: "spin", icon: Orbit, on: autoRotate, label: "Turntable", act: toggleTurntable },
            { key: "reset", icon: RotateCcw, on: false, label: "Reset view", act: resetView },
            { key: "full", icon: Maximize2, on: false, label: "Fullscreen", act: goFullscreen },
          ].map(({ key, icon: Icon, on, label, act }) => (
            <motion.button
              key={key}
              onClick={act}
              title={label}
              aria-label={label}
              whileHover={reduce ? undefined : { scale: 1.1 }}
              whileTap={reduce ? undefined : { scale: 0.9 }}
              className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${
                on ? "bg-[var(--accent,#a0793d)] text-white" : "text-ink-600 hover:bg-black/5 hover:text-ink-900"
              }`}
            >
              <Icon size={16} strokeWidth={1.9} />
            </motion.button>
          ))}
        </motion.div>
      )}

      {ready && !loading && (groups.length > 0 || variants.length > 1) && (
        <CustomerBar
          name={data.name}
          groups={groups}
          variants={variants}
          activeVariant={activeVariant}
          swapping={swapping}
          basePrice={meta.basePrice}
          currency={meta.currency}
          onPick={(groupId, idx) => void applyIndex(groupId, idx)}
          onVariant={(vi) => void selectVariant(vi)}
        />
      )}
    </>
  );
}
